import AbortError from '@hinokuni/sdk/errors/AbortError'
import HinokuniError from '@hinokuni/sdk/errors/HinokuniError'
import { router, usePage } from '@inertiajs/vue3'
import BucketError from '@/components/annotator/interfaces/data/BucketError.js'

// eslint-disable-next-line no-undef
const sentryEnabled = VITE_SENTRY_ENABLED?.toLowerCase() === 'true'
const ignoredNetworkErrorMessages = [
  'failed to fetch',
  'load failed',
  'networkerror when attempting to fetch resource.',
  'network request failed',
  'the internet connection appears to be offline.',
]

function isIgnoredNetworkError(error) {
  if (error instanceof AbortError) {
    return true
  }

  if (!(error instanceof Error)) {
    return false
  }

  const message = error.message?.trim().toLowerCase()
  return ignoredNetworkErrorMessages.includes(message)
}

function isIgnoredBucketError(error) {
  if (!(error instanceof BucketError)) {
    return false
  }

  if (error.status === 404) {
    return true
  }

  return error.status === 403 && error.isUrlExpired
}

function errorMiddleware(error) {
  if (isIgnoredNetworkError(error)) {
    return null
  }

  if (isIgnoredBucketError(error)) {
    return null
  }

  const page = usePage()
  if (error instanceof HinokuniError) {
    switch (error.status) {
      case 401:
        router.visit(`/signin?next=${page.url}`)
        return null
    }
  }
  return error
}

function getEnvironment() {
  switch (location.hostname) {
    case 'app.picsellia.com':
      return 'production'
    case 'staging.picsellia.com':
      return 'staging'
    case 'trial.picsellia.com':
      return 'trial'
    default:
      return 'development'
  }
}

export default async function useErrorMiddleware(enabled, app) {
  if (enabled && sentryEnabled) {
    const sentry = await import('@sentry/vue')

    sentry.init({
      app,
      dsn: 'https://a1d79dc1bf934e9695c4d519d572316b@o1044018.ingest.sentry.io/4505466000113664',
      integrations: [
        sentry.browserTracingIntegration({
          instrumentNavigation: false,
          instrumentPageLoad: false,
        }),
      ],
      tracesSampleRate: 0.01,
      environment: getEnvironment(),
      tracePropagationTargets: [
        /^https:\/\/staging.picsellia\.com\//,
        /^https:\/\/trial.picsellia\.com\//,
        /^https:\/\/app.picsellia\.com\//,
      ],
      beforeSend(event, hint) {
        if (!hint?.originalException) {
          return event
        }
        const error = errorMiddleware(hint.originalException)
        return error ? event : null
      },
    })

    router.on('start', (event) => {
      const span = sentry.startInactiveSpan({
        name: event.detail.visit.url.pathname,
        op: 'navigation',
        attributes: {
          [sentry.SEMANTIC_ATTRIBUTE_SENTRY_SOURCE]: 'url',
        },
      })

      event.detail.visit._sentrySpan = span
    })

    router.on('navigate', (event) => {
      const span = event.detail.visit?._sentrySpan
      if (span) {
        span.updateName(event.detail.page.component)
        span.setAttribute(sentry.SEMANTIC_ATTRIBUTE_SENTRY_SOURCE, 'route')
      }
    })

    router.on('finish', (event) => {
      const span = event.detail.visit?._sentrySpan
      if (span) {
        span.end()
      }
    })

    sentry.setContext('screen', {
      width: screen.width,
      height: screen.height,
      availWidth: screen.availWidth,
      availHeight: screen.availHeight,
      devicePixelRatio: window.devicePixelRatio,
    })

    app.provide('sentry', {
      setUser: sentry.setUser,
      setContext: sentry.setContext,
      setTag: sentry.setTag,
      captureException: sentry.captureException,
    })

    app.config.warnHandler = (msg, instance, trace) => {
      sentry.captureMessage(msg, {
        level: 'warning',
        extra: {
          trace,
          component: instance?.$options.name || instance?.$options.__file || 'Unknown',
        },
        tags: {
          source: 'vue',
        },
      })
    }
  }
  else {
    app.provide('sentry', {
      setUser: () => {},
      setContext: () => {},
      setTag: () => {},
      captureException: () => {},
    })

    app.config.errorHandler = (_error) => {
      const error = errorMiddleware(_error)
      if (error) {
        console.error(error)
      }
    }
  }
}
